import { Box, styled, Typography } from "@material-ui/core";
import { CheckCircleOutline, ErrorOutline } from "@material-ui/icons";
import { format } from "date-fns";
import type { FC } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_STATUS_DRAFT_BG,
  EPOCUS_STATUS_DRAFT_TEXT,
  EPOCUS_SYNCED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import type { EpocusOpsAuditEvent } from "./audit-trail-operations";

const OPERATION_LABELS: Record<string, string> = {
  "Mutation.createEpocusProgram": "Created the program",
  "Mutation.updateEpocusProgram": "Changed program settings",
  "Mutation.setEpocusProgramEnabledExamTypes": "Changed the enabled exam types",
  "Mutation.provisionEpocusProgramMember": "Added a team member",
  "Mutation.addEpocusProgramMember": "Joined the program",
  "Mutation.updateEpocusUser": "Updated a team member",
  "Mutation.sendEpocusTestStudy": "Sent a test scan",
};

const Row = styled(Box)({
  display: "flex",
  gap: 12,
  padding: "12px 0",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  "&:last-child": { borderBottom: "none" },
});

const IconSlot = styled(Box)({
  display: "flex",
  flexShrink: 0,
  marginTop: 2,
});

const Body = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
  flex: 1,
});

const ActorLine = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
});

const AgentBadge = styled("span")({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: EPOCUS_ICON_ACTIVE,
  border: `1px solid ${EPOCUS_ICON_ACTIVE}`,
  borderRadius: 4,
  padding: "0 5px",
});

const FailureNote = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_STATUS_DRAFT_TEXT,
  backgroundColor: EPOCUS_STATUS_DRAFT_BG,
  borderRadius: 6,
  padding: "4px 8px",
  marginTop: 4,
  wordBreak: "break-word",
});

const describeDetail = (detail: unknown): string | null => {
  if (!detail || typeof detail !== "object") return null;
  const fields = detail as Record<string, unknown>;

  if (typeof fields.dimseAeTitle === "string") {
    return `AE title ${fields.dimseAeTitle}`;
  }
  if (typeof fields.email === "string") {
    const role = typeof fields.role === "string" ? ` as ${fields.role}` : "";
    return `${fields.email}${role}`;
  }
  if (Array.isArray(fields.enabledExamTypes)) {
    return fields.enabledExamTypes.length === 0
      ? "All exam types"
      : fields.enabledExamTypes.join(", ");
  }
  if (Array.isArray(fields.changed) && fields.changed.length > 0) {
    return fields.changed.join(", ");
  }
  return null;
};

export const AuditEventRow: FC<{ event: EpocusOpsAuditEvent }> = ({
  event,
}) => {
  const failed = event.outcome === "FAILURE";
  const byAgent = event.actorKind === "MACHINE";
  const detail = describeDetail(event.detail);

  return (
    <Row>
      <IconSlot>
        {failed ? (
          <ErrorOutline
            fontSize="small"
            style={{ color: EPOCUS_STATUS_DRAFT_TEXT }}
            aria-label="Did not complete"
          />
        ) : (
          <CheckCircleOutline
            fontSize="small"
            style={{ color: EPOCUS_SYNCED_TEXT }}
            aria-label="Completed"
          />
        )}
      </IconSlot>

      <Body>
        <Typography style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}>
          {OPERATION_LABELS[event.operation] ?? event.operation}
        </Typography>

        <ActorLine>
          {byAgent && <AgentBadge>Agent</AgentBadge>}
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            {byAgent
              ? `${event.actorId}, on behalf of ${event.onBehalfOfId ?? "an unknown admin"}`
              : event.actorId}
            {" · "}
            {format(new Date(event.occurredAt), "d MMM yyyy, HH:mm")}
          </Typography>
        </ActorLine>

        {detail && (
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            {detail}
          </Typography>
        )}

        {failed && event.failureReason && (
          <FailureNote>{event.failureReason}</FailureNote>
        )}
      </Body>
    </Row>
  );
};
